import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const Shell = ({ children }) => (
  <div className="min-h-screen bg-cream flex items-center justify-center p-6">
    <div className="w-full max-w-md">
      <div className="text-center mb-6">
        <Link to="/" className="inline-flex flex-col items-center" data-testid="verify-email-logo">
          <img src="/ato-logo.png" alt="ATÔ" className="w-[160px] md:w-[200px] h-auto object-contain" />
        </Link>
      </div>
      {children}
    </div>
  </div>
);

/* `/verify-email?token=...` — the landing page for the EMAIL_VERIFICATION
   link. One-shot: calls the backend on mount, shows success/failure. */
export const VerifyEmailPage = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = (params.get('token') || '').trim();
  const { refreshUser, user } = useAuth();
  const [phase, setPhase] = useState('verifying'); // verifying | success | failed
  const requestedRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setPhase('failed');
      return;
    }
    // Guard against React.StrictMode's dev-only double-invoke of effects —
    // this call CONSUMES the token server-side, a 2nd request would always
    // fail even though the first one already succeeded.
    if (requestedRef.current) return;
    requestedRef.current = true;
    // NOTE: no per-effect `alive` flag here — under StrictMode the first
    // effect's cleanup would flip it to false while the ref guard stops the
    // second effect from re-requesting, leaving the page stuck on the spinner.
    axios.get(`${API}/api/auth/verify-email`, { params: { token } })
      .then(() => {
        setPhase('success');
        if (user) refreshUser();
      })
      .catch(() => {
        setPhase('failed');
      });
  }, [token]);

  if (phase === 'verifying') {
    return (
      <Shell>
        <div className="bg-white border border-border rounded-2xl p-8 shadow-sm text-center" data-testid="verify-email-loading">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-text_muted">{t('emailVerify.verifying')}</p>
        </div>
      </Shell>
    );
  }

  if (phase === 'success') {
    return (
      <Shell>
        <div className="bg-white border border-border rounded-2xl p-8 shadow-sm text-center" data-testid="verify-email-success">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-success-soft mb-4">
            <CheckCircle size={26} weight="fill" className="text-success" />
          </div>
          <h2 className="text-lg md:text-xl font-medium text-text_strong tracking-wide mb-3">
            {t('emailVerify.successTitle')}
          </h2>
          <p className="text-text_muted mb-6">{t('emailVerify.successDesc')}</p>
          <Link to={user ? '/' : '/login'}>
            <Button variant="primary" className="w-full py-3 font-semibold" data-testid="verify-email-continue-btn">
              {user ? t('emailVerify.goToHome') : t('emailVerify.goToLogin')}
            </Button>
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="bg-white border border-border rounded-2xl p-8 shadow-sm text-center" data-testid="verify-email-failed">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-danger-soft mb-4">
          <WarningCircle size={26} weight="fill" className="text-danger" />
        </div>
        <h2 className="text-lg md:text-xl font-medium text-text_strong tracking-wide mb-2">
          {t('emailVerify.failedTitle')}
        </h2>
        <p className="text-text_muted mb-6">{t('emailVerify.failedDesc')}</p>
        <Link to={user ? '/' : '/login'}>
          <Button variant="outline" className="w-full py-3 font-semibold" data-testid="verify-email-back-btn">
            {user ? t('emailVerify.goToHome') : t('emailVerify.goToLogin')}
          </Button>
        </Link>
      </div>
    </Shell>
  );
};
